// src/utils/emailPreview.js
// Utility functions for building email previews with footer

import { safeGreetingFirstName } from './greetingName';

/**
 * Build complete email preview with footer and signature
 * @param {string} messageBody - The main message body
 * @param {string} senderName - The sender's name (for signature variable)
 * @param {string} signaturePosition - Where to place signature (default: 'below')
 * @returns {string} - Complete email with footer
 */
export function buildEmailPreview(messageBody, senderName = '', signaturePosition = 'below') {
  if (!messageBody) return '';
  
  const trimmedBody = messageBody.trim();
  
  // Default footer format: "Best,\nSignature"
  const signaturePlaceholder = senderName || '{YourName}';
  
  let footer = '';
  if (signaturePosition === 'below') {
    footer = `\n\nBest,\n${signaturePlaceholder}`;
  } else if (signaturePosition === 'inline') {
    footer = `\n\nBest,\n${signaturePlaceholder}`;
  }
  
  return trimmedBody + footer;
}

/**
 * Extract first name from contact name
 *
 * This used to be `contactName.split(',')[0].split(/\s+/)[0]` — everything
 * before the first space, whatever it was. On 2026-09-22 a campaign row whose
 * contact cell read exactly "Not provided" got the product's first outreach
 * email opening "Hi Not,". It now answers with a name or with nothing, under
 * the one shared rule (`./greetingName`, mirrored from
 * `functions/lib/greetingName.js`, ported from
 * `email_automation/greeting_name.py`).
 *
 * Nothing is the empty string, which is what every caller already treats as
 * "no name": the greeting falls back to the neutral "Hi," these paths already
 * compose, and campaign launch raises its pre-existing blocking recipient
 * issue so the operator can supply the real name.
 */
export function extractFirstName(contactName) {
  return safeGreetingFirstName(contactName) || '';
}

const NAME_PLACEHOLDER_SOURCE = String.raw`\[(?:first\s*)?name\]|\{\{\s*(?:firstName|first_name|name)\s*\}\}|\{\s*(?:firstName|first_name|name)\s*\}`;
const EXPLICIT_GENERIC_RECIPIENT_SOURCE = String.raw`sir(?:\/|\s+or\s+)madam|leasing agent|property owner|team|there|everyone|everybody|folks|all`;
const STRONG_GREETING_SOURCE = String.raw`good\s+(?:morning|afternoon|evening|day)|greetings|hello|dear|hey|hi`;
const WEAK_GREETING_SOURCE = String.raw`morning|afternoon|evening`;
const WEAK_GREETING_PATTERN = new RegExp(`^(?:${WEAK_GREETING_SOURCE})$`, 'i');
const LEADING_GREETING_PATTERN = new RegExp(
  `^([\\s\\uFEFF]*)(${STRONG_GREETING_SOURCE}|${WEAK_GREETING_SOURCE})(?=[\\s,!?:;.–—[{]|$)`,
  'i'
);
const LEADING_GREETING_HEAD_PATTERN = new RegExp(
  `^[\\s\\uFEFF]*(${STRONG_GREETING_SOURCE}|${WEAK_GREETING_SOURCE})`
  + `(?=[\\s,!?:;.\u2013\u2014[{]|$)`
  + `((?:[\\t ]*[,!?:;.\u2013\u2014-]+[\\t ]*)|[\\t ]+)?`,
  'i'
);
const EXPLICIT_PLACEHOLDER_PATTERN = new RegExp(
  `^(?:${NAME_PLACEHOLDER_SOURCE})(?=$|[\\s,!?:;.–—-])`,
  'i'
);
const EXPLICIT_GENERIC_RECIPIENT_PATTERN = new RegExp(
  `^(?:${EXPLICIT_GENERIC_RECIPIENT_SOURCE})(?=(?:[\\s\\uFEFF]*(?:[,!?:;.]|[–—]|-(?=[\\s\\uFEFF]))|[\\s\\uFEFF]*$))`,
  'i'
);
const SIGN_OFF_LINE_PATTERN = /^(?:Best regards|Kind regards|Warm regards|All the best|Take care|Many thanks|With thanks|Yours truly|Best wishes|Talk soon|Looking forward|Thank you|Respectfully|Cordially|Sincerely|Regards|Cheers|Thanks|Best)[,!]?$/i;
const CAMPAIGN_BODY_FIELD_LINE_PATTERN = /^(?:Additional Details|Asking Rent|Availability|Available SF|Broker Contact|CAM|Ceiling Ht|CRE|Docks|Drive Ins|Flyer(?: \/)? Link|Floor ?Plan|Floorplan|Leasing (?:Company )?Contact|Listing Broker(?: Contact)?|Loading Access|NOI|Operating Expenses|Power|Property Details|Property Metrics|Property Owner|Questions? (?:&|and) Answers?|Rent|Rent\/SF \/?Yr|Tenant Rep|Total SF):?$/i;

function consumeExplicitGreetingSeparator(raw, start) {
  let cursor = consumeGreetingTransportSpacing(raw, start);
  const separator = raw.slice(cursor).match(
    /^(?:[,!?:;.]+|[–—]|-(?=[\s\uFEFF]))/u
  );
  if (separator) cursor += separator[0].length;
  return consumeGreetingTransportSpacing(raw, cursor);
}

function consumeRecipientTerminator(raw, start) {
  let cursor = consumeGreetingTransportSpacing(raw, start);
  const terminator = raw.slice(cursor).match(
    /^(?:[,!?:;.]+|[–—]|-(?=[\s\uFEFF]))/u
  );
  if (terminator) cursor += terminator[0].length;
  return consumeGreetingTransportSpacing(raw, cursor);
}

function consumeGreetingTransportSpacing(raw, start) {
  const spacing = raw.slice(start).match(
    /^(?:[\s\uFEFF])+/u
  );
  return spacing ? start + spacing[0].length : start;
}

/**
 * Classify only the syntax at the leading greeting boundary. Literal names and
 * body prose are intentionally opaque: without an explicit protocol token,
 * interpreting either would require guessing which code units are disposable.
 */
export function parseLeadingGreetingBoundary(rawValue) {
  const raw = typeof rawValue === 'string' ? rawValue : '';
  const greetingMatch = raw.match(LEADING_GREETING_PATTERN);
  if (!greetingMatch) return { kind: 'none' };

  const greeting = greetingMatch[2];
  const greetingEnd = greetingMatch[0].length;
  const isWeak = WEAK_GREETING_PATTERN.test(greeting);
  const hasAttachedUnicodeDash = /^[–—]/u.test(raw.slice(greetingEnd));
  const recipientStart = consumeExplicitGreetingSeparator(raw, greetingEnd);
  const recipientRemainder = raw.slice(recipientStart);
  const explicitRecipient = recipientRemainder.match(EXPLICIT_PLACEHOLDER_PATTERN)
    || recipientRemainder.match(EXPLICIT_GENERIC_RECIPIENT_PATTERN);
  if (explicitRecipient) {
    const recipientEnd = recipientStart + explicitRecipient[0].length;
    return {
      kind: 'recognized',
      greeting,
      bodyStart: consumeRecipientTerminator(raw, recipientEnd),
      wrapperEnd: recipientEnd
    };
  }

  if (hasAttachedUnicodeDash) {
    return {
      kind: 'ambiguous',
      greeting,
      isWeak,
      preserveStart: 0
    };
  }

  const bareWrapper = raw.slice(greetingEnd).match(
    /^(?:[\t ]*[,!?:;.]*[\t ]*|[\t ]*[–—][\t ]*|[\t ]+-[\t ]*)\r?\n[\t ]*\r?\n/u
  );
  if (!isWeak && bareWrapper) {
    const bodyStart = greetingEnd + bareWrapper[0].length;
    const possibleSplitRecipient = raw.slice(bodyStart).match(/^([^\r\n]{1,100})(?:\r?\n[\t ]*\r?\n)/u);
    if (
      possibleSplitRecipient
      && /(?:[,;:]|[–—]|\s-)$/u.test(possibleSplitRecipient[1].trim())
    ) {
      return {
        kind: 'ambiguous',
        greeting,
        isWeak,
        preserveStart: 0
      };
    }
    return {
      kind: 'recognized',
      greeting,
      bodyStart,
      wrapperEnd: bodyStart
    };
  }

  const bareGreetingOnly = raw.slice(greetingEnd).match(/^[\t ,!?:;.–—-]*$/u);
  if (!isWeak && bareGreetingOnly) {
    return {
      kind: 'recognized',
      greeting,
      bodyStart: raw.length,
      wrapperEnd: raw.length
    };
  }

  return {
    kind: 'ambiguous',
    greeting,
    isWeak,
    preserveStart: isWeak ? 0 : recipientStart
  };
}

/**
 * Locate just the greeting WORD and the separator that follows it. Unlike
 * parseLeadingGreetingBoundary this makes no claim about the addressee - it
 * only says where the greeting stops, so a caller that is allowed to rewrite
 * (the uploaded-script seam) can decide what to do with what comes next.
 */
export function matchLeadingGreetingHead(rawValue) {
  const raw = typeof rawValue === 'string' ? rawValue : '';
  const match = raw.match(LEADING_GREETING_HEAD_PATTERN);
  if (!match) return null;
  return { greeting: match[1], restStart: match[0].length };
}

function normalizeCampaignBodyFieldLine(value) {
  return String(value || '')
    .trim()
    .replace(/:$/, '')
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function isCampaignBodyFieldLine(value, bodyFieldLabels = []) {
  const normalized = normalizeCampaignBodyFieldLine(value);
  return CAMPAIGN_BODY_FIELD_LINE_PATTERN.test(String(value || '').trim())
    || (Array.isArray(bodyFieldLabels) && bodyFieldLabels.some(
      label => normalizeCampaignBodyFieldLine(label) === normalized
    ));
}

export function normalizeNamePlaceholders(script) {
  if (typeof script !== 'string') return script;
  return script.replace(new RegExp(NAME_PLACEHOLDER_SOURCE, 'gi'), '[NAME]');
}

/**
 * Strip common email sign-offs from the end of a script
 * Since signatures are handled separately, we remove endings like "Best,", "Thanks,", etc.
 */
export function stripSignOff(script, { bodyFieldLabels = [] } = {}) {
  if (!script) return script;
  const trimmed = String(script).trim();
  const lines = [];
  const lineStarts = [];
  const lineBreakPattern = /\r\n?|\n/g;
  let lineStart = 0;
  let lineBreak;
  while ((lineBreak = lineBreakPattern.exec(trimmed)) !== null) {
    lineStarts.push(lineStart);
    lines.push(trimmed.slice(lineStart, lineBreak.index));
    lineStart = lineBreak.index + lineBreak[0].length;
  }
  lineStarts.push(lineStart);
  lines.push(trimmed.slice(lineStart));

  for (let index = lines.length - 1; index > 0; index -= 1) {
    if (!SIGN_OFF_LINE_PATTERN.test(lines[index].trim())) continue;
    const trailingLines = lines.slice(index + 1);
    const contentLines = trailingLines.map(line => line.trim()).filter(Boolean);
    const hasBlankSeparatedContent = trailingLines.length > 1 && !trailingLines[0].trim();
    const isCampaignBodyFieldBlock = (
      hasBlankSeparatedContent
      && contentLines.length > 0
      && isCampaignBodyFieldLine(contentLines[0], bodyFieldLabels)
    );
    if (isCampaignBodyFieldBlock) return trimmed;
    return trimmed.slice(0, lineStarts[index]).trim();
  }

  return trimmed;
}

/**
 * Normalize only an explicit placeholder/generic greeting protocol to [NAME].
 * Literal addressees and greeting-shaped prose stay untouched because their
 * boundary cannot be inferred without risking body loss.
 * Also strips sign-offs since signatures are handled separately.
 */
export function normalizeScriptWithPlaceholder(script, { bodyFieldLabels = [] } = {}) {
  if (!script) return script;

  const boundary = parseLeadingGreetingBoundary(script);
  if (boundary.kind === 'ambiguous') return script;

  if (boundary.kind === 'recognized') {
    const body = normalizeNamePlaceholders(script.slice(boundary.bodyStart));
    const normalizedBody = stripSignOff(body, { bodyFieldLabels });
    return `${boundary.greeting} [NAME],\n\n${normalizedBody}`;
  }

  const normalized = stripSignOff(normalizeNamePlaceholders(script), { bodyFieldLabels });

  // If it already has [NAME] placeholder, ensure proper spacing after greeting
  if (/\[NAME\]/i.test(normalized)) {
    // Ensure there's a blank line after the greeting (Hi [NAME],)
    return ensureBlankLineAfterGreeting(normalized);
  }

  return normalized;
}

/**
 * Ensure there's a blank line after the greeting line
 * Handles malformed greetings from Excel imports like:
 * - "Hi [NAME]\n,\n\nI found..." (comma on separate line)
 * - "Hi [NAME]\n\n,\n\nI found..." (comma on separate line with extra blank)
 * - "Hi[NAME]," (no space)
 * - "  Hi [NAME] , " (extra whitespace)
 */
function ensureBlankLineAfterGreeting(script) {
  // Normalize line endings first (Windows \r\n -> \n)
  let fixed = script.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  // Single robust regex that handles all variations:
  // - Captures greeting word (Hi/Hello/Dear)
  // - Handles any whitespace between greeting and [NAME]
  // - Handles any whitespace between [NAME] and comma (including newlines)
  // - Handles any whitespace after the comma
  // Result: "Greeting [NAME],\n\n" followed by the rest of the message
  fixed = fixed.replace(
    /^\s*(Hi|Hello|Dear)\s*\[NAME\]\s*,\s*/i,
    '$1 [NAME],\n\n'
  );

  return fixed;
}

/**
 * Inject recipient name into email script
 * Replaces [NAME] placeholder with actual name
 *
 * NOTE: Only [NAME] is supported. Property address is in the subject line.
 */
export function injectNameIntoScript(originalScript, recipientName) {
  if (!originalScript) return originalScript;

  const normalized = normalizeNamePlaceholders(originalScript);
  if (!recipientName) {
    return replaceUnresolvedNameWithGenericGreeting(normalized);
  }

  const firstName = extractFirstName(recipientName);
  if (!firstName) {
    return replaceUnresolvedNameWithGenericGreeting(normalized);
  }

  // Replace placeholder with actual name (case-insensitive)
  const personalized = normalized
    .replace(/\[NAME\]/gi, firstName)
    .replace(/\{\{name\}\}/gi, firstName)
    .replace(/\{name\}/gi, firstName)
    .replace(/\{firstName\}/gi, firstName)
    .replace(/\{\{firstName\}\}/gi, firstName);

  return personalized;
}

function replaceUnresolvedNameWithGenericGreeting(script) {
  return script
    .replace(/^\s*(Hi|Hello)\s+\[NAME\]\s*,\s*/i, '$1,\n\n')
    .replace(/^\s*Dear\s+\[NAME\]\s*,\s*/i, 'Hello,\n\n')
    .replace(/\[NAME\]/gi, '')
    .replace(/\{\{name\}\}/gi, '')
    .replace(/\{name\}/gi, '')
    .replace(/\{firstName\}/gi, '')
    .replace(/\{\{firstName\}\}/gi, '');
}

/**
 * Build personalized email preview for a recipient
 */
export function buildPersonalizedPreview(messageBody, recipientName, senderName = '', signaturePosition = 'below') {
  const personalizedBody = injectNameIntoScript(messageBody, recipientName);
  return buildEmailPreview(personalizedBody, senderName, signaturePosition);
}
